//src/hooks/useVCardForm.ts
import { useEffect, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useAuthStore } from "../store/authStore";
import { useUserStore } from "../store/userStore";
import { useVCardStore } from "../store/vcardStore";
import { useDebugStore } from "../store/debugStore";
import type { VCard, VCardCategory } from "../types/vcard";

interface UseVCardFormProps {
  category: VCardCategory;
  onToggleActive?: (
    category: VCardCategory,
    isActive: boolean,
  ) => Promise<void>;
}

type VCardFormData = Partial<VCard> & {
  middleName?: string;
  mobilePhoneCountryCode?: string;
  workPhoneCountryCode?: string;
  travelPhoneCountryCode?: string;
};

type FormFieldElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement;

const setNestedValue = (
  source: Record<string, unknown>,
  parts: string[],
  value: unknown,
): Record<string, unknown> => {
  const result: Record<string, unknown> = { ...source };
  let current: Record<string, unknown> = result;

  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const existingValue = current[key];

    if (
      typeof existingValue !== "object" ||
      existingValue === null ||
      Array.isArray(existingValue)
    ) {
      current[key] = {};
    }

    current = current[key] as Record<string, unknown>;
  }

  current[parts[parts.length - 1]] = value;
  return result;
};

export const useVCardForm = ({ category }: UseVCardFormProps) => {
  const user = useAuthStore((state) => state.user);
  const userProfile = useUserStore((state) => state.profile);
  const { vcards, createVCard, updateVCard } = useVCardStore();
  const addDebugMessage = useDebugStore((state) => state.addMessage);

  const [formData, setFormData] = useState<VCardFormData>({
    category,
    isActive: false,
    version: "4.0",
  });
  const [isFormDataReady, setIsFormDataReady] = useState(false);

  // Load existing data
  useEffect(() => {
    if (!user || !userProfile) {
      setIsFormDataReady(false);
      return;
    }

    addDebugMessage("info", `Loading ${category} vCard data`);

    const homeVCard = vcards.find(
      (v) => v.userId === user.id && v.category === "home",
    ) as VCardFormData | undefined;

    const categoryVCard = vcards.find(
      (v) => v.userId === user.id && v.category === category,
    ) as VCardFormData | undefined;

    // Base information from user profile
    const baseInfo = {
      firstName: userProfile.firstName || "",
      middleName: "",
      lastName: userProfile.lastName || "",
      email: userProfile.email || "",
      phone: userProfile.phone || "",
      mobilePhone: userProfile.phone || "",
      mobilePhoneCountryCode: "+61",
      workPhone: userProfile.workPhone || "",
      workPhoneCountryCode: "+61",
      travelPhone: "",
      travelPhoneCountryCode: "+61",
      messagingContact: userProfile.messagingContact || null,
      messagingContacts: Array.isArray(userProfile.messagingContacts)
        ? userProfile.messagingContacts
        : userProfile.messagingContact
          ? [userProfile.messagingContact]
          : [],
      address: userProfile.address || null,
    };

    if (category === "home") {
      setFormData({
        ...baseInfo,
        category,
        isActive: true,
        userId: user.id,
        version: "4.0",
        formattedName: userProfile.name,
        ...(homeVCard || {}),
      });
    } else if (homeVCard) {
      const homeInfo = {
        firstName: homeVCard.firstName || baseInfo.firstName,
        middleName: homeVCard.middleName || baseInfo.middleName,
        lastName: homeVCard.lastName || baseInfo.lastName,
        email: homeVCard.email || homeVCard.personalEmail || baseInfo.email,
        mobilePhone: homeVCard.mobilePhone || baseInfo.mobilePhone,
        mobilePhoneCountryCode:
          homeVCard.mobilePhoneCountryCode || baseInfo.mobilePhoneCountryCode,
        phone: homeVCard.phone || baseInfo.phone,
        workPhone: homeVCard.workPhone || baseInfo.workPhone,
        workPhoneCountryCode:
          homeVCard.workPhoneCountryCode || baseInfo.workPhoneCountryCode,
        travelPhone: homeVCard.travelPhone || baseInfo.travelPhone,
        travelPhoneCountryCode:
          homeVCard.travelPhoneCountryCode || baseInfo.travelPhoneCountryCode,
        messagingContact:
          homeVCard.messagingContact || baseInfo.messagingContact,
        messagingContacts: Array.isArray(homeVCard.messagingContacts)
          ? homeVCard.messagingContacts
          : homeVCard.messagingContact
            ? [homeVCard.messagingContact]
            : baseInfo.messagingContacts,
        address: homeVCard.address || baseInfo.address,
      };

      if (categoryVCard) {
        setFormData({
          ...homeInfo,
          ...categoryVCard,
          categorySpecificInfo: categoryVCard.categorySpecificInfo || {},
          category,
          isActive: categoryVCard.isActive,
        });
      } else {
        setFormData({
          ...homeInfo,
          category,
          isActive: false,
          userId: user.id,
          version: "4.0",
          categorySpecificInfo: {
            [category]: {},
          },
        });
      }
    } else {
      setFormData({
        ...baseInfo,
        category,
        isActive: false,
        userId: user.id,
        version: "4.0",
        formattedName: userProfile.name,
      });
    }

    setIsFormDataReady(true);
    addDebugMessage("info", `${category} vCard data loaded`);
  }, [user, userProfile, vcards, category, addDebugMessage]);

  const handleChange = (e: ChangeEvent<FormFieldElement>) => {
    const { name, value } = e.target;

    // Handle isActive changes immediately
    if (name === "isActive") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "true" || value === true,
      }));
      return;
    }

    setFormData((prev) => {
      if (name.includes(".")) {
        const parts = name.split(".");
        return setNestedValue(
          prev as Record<string, unknown>,
          parts,
          value,
        ) as VCardFormData;
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user) throw new Error("User not authenticated");

    try {
      addDebugMessage(
        "info",
        `Submitting ${category} vCard with data:`,
        formData,
      );

      const vcardData = {
        ...formData,
        userId: user.id,
        category,
        formattedName:
          `${formData.firstName} ${formData.middleName} ${formData.lastName}`.trim(),
        // Ensure address is properly structured
        address: formData.address
          ? {
              street: formData.address.street || "",
              unit: formData.address.unit || "",
              city: formData.address.city || "",
              state: formData.address.state || "",
              zip: formData.address.zip || "",
              country: formData.address.country || "",
            }
          : null,
      };

      addDebugMessage("info", `Processed vCard data:`, vcardData);

      if (formData.id) {
        await updateVCard(formData.id, vcardData);
        addDebugMessage("info", `Updated ${category} vCard`);
      } else {
        await createVCard(vcardData);
        addDebugMessage("info", `Created new ${category} vCard`);
      }
    } catch (error) {
      addDebugMessage("error", `Failed to save ${category} vCard`, error);
      throw error;
    }
  };

  return {
    formData,
    handleChange,
    handleSubmit,
    setFormData,
    isFormDataReady,
  };
};

export default useVCardForm;
